import React, { useEffect, useState } from "react";
import axios from "axios";

import PageHeader from "../components/ui/PageHeader";
import StatCard from "../components/ui/StatCard";
import Card from "../components/ui/Card";
import Badge from "../components/ui/Badge";
import Button from "../components/ui/Button";
import FormField from "../components/ui/FormField";

import { statusColor } from "../utils/helpers";
import useAuth from "../hooks/useAuth";
import { useSeason } from "../context/SeasonContext";
import { FiRefreshCw } from "react-icons/fi";
import "./Production.css";

const API = "https://maize.norissolutions.in/Php/public/api";

export default function Production() {
  const token = sessionStorage.getItem("maaze_token");
  const { refreshToken } = useAuth();

  const [batches, setBatches] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [stats, setStats] = useState({
    sent_to_factory: 0,
    in_drying: 0,
    dried_ready: 0,
    finished_goods: 0,
  });

  const [form, setForm] = useState({
    season_id: "",
    batch_no: "",
    input_qty_kg: "",
    start_date: "",
    end_date: "",
    process_type: "Drying",
    output_qty_kg: "",
    status: "In Progress",
    supervisor_id: "",
    notes: "",
  });

  const [deliveryForm, setDeliveryForm] = useState({
    batch_id: "",
    season_id: "",
    client_name: "",
    delivery_date: "",
    quantity_kg: "",
    rate_per_kg: "",
    vehicle_no: "",
    payment_status: "Pending",
    invoice_no: "",
  });

  const timelineSteps = [
    {
      title: "Crop collected from farmers",
      desc: "Raw crop collected",
      date: "Completed",
      done: true,
    },
    {
      title: "Quality inspection",
      desc: "Quality testing completed",
      date: "Completed",
      done: true,
    },
    {
      title: "Sorted & weighed",
      desc: "Sorting process completed",
      date: "Completed",
      done: true,
    },
    {
      title: "Dispatched to factory",
      desc: "Batch created",
      date: batches.length ? batches[0].start_date : "Pending",
      done: batches.length > 0,
    },
    {
      title: "Drying in progress",
      desc: "Production batch running",
      date: batches.length ? "In Progress" : "Pending",
      done: false,
    },
    {
      title: "Quality check – dried",
      desc: "Final quality verification",
      date: "Pending",
      done: false,
    },
    {
      title: "Finished goods delivery",
      desc: deliveries.length
        ? `${deliveries[0].client_name} · ${deliveries[0].quantity_kg} kg`
        : "Pending",
      date: deliveries.length ? deliveries[0].delivery_date : "Pending",
      done: deliveries.length > 0,
    },
  ];

  /* ================= FETCH APIs ================= */
  const fetchCompanies = async (seasonId) => {
    if (!seasonId) return;

    try {
      const res = await axios.get(
        `${API}/companies?season_id=${seasonId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setCompanies(res.data?.data?.data || []);
    } catch (err) {
      console.error(err);
    }
  };
  const { selectedSeason } = useSeason() || {};
  
  useEffect(() => {
    if (selectedSeason) {
      fetchCompanies(selectedSeason);
      
      setForm((prev) => ({
        ...prev,
        season_id: selectedSeason,
      }));
    }
  }, [selectedSeason]);
  const fetchTimeline = async () => {
    if (!selectedSeason) return;
    try {
      const res = await axios.get(
        `${API}/dashboard/production-timeline?season_id=${selectedSeason}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (res.data?.data) {
        setTimeline(res.data.data);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const fetchBatches = async () => {
    const res = await axios.get(`${API}/production/batches`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    setBatches(res.data?.data || []);
  };

  const fetchDeliveries = async () => {
    const res = await axios.get(`${API}/production/deliveries`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    setDeliveries(res.data?.data || []);
  };

  const fetchSeasons = async () => {
    const res = await axios.get(`${API}/seasons`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    setSeasons(res.data?.data || []);
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/dashboard/production-cards`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data?.data) {
        setStats(res.data.data);
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchBatches();
    fetchDeliveries();
    fetchSeasons();
    fetchStats();
    fetchTimeline();
  }, []);

  /* ================= CREATE BATCH ================= */

  const handleSubmit = async () => {
    // const payload = {
    //   batch_no: form.batch_no,
    //   input_qty_kg: Number(form.input_qty_kg),
    //   start_date: form.start_date,
    //   end_date: form.end_date,
    //   process_type: form.process_type,
    //   output_qty_kg: Number(form.output_qty_kg),
    //   status: form.status,
    //   supervisor_id: Number(form.supervisor_id),
    //   notes: form.notes,
    // };
    const payload = {
      batch_no: form.batch_no,
      batch_owner_type: form.batch_owner_type,
      company_id:
        form.batch_owner_type === "External"
          ? Number(form.company_id)
          : null,
      input_qty_kg: Number(form.input_qty_kg),
      start_date: form.start_date,
      end_date: form.end_date,
      process_type: form.process_type,
      output_qty_kg: Number(form.output_qty_kg),
      status: form.status,
      notes: form.notes,
    };

    await axios.post(`${API}/production/batches`, payload, {
      headers: { Authorization: `Bearer ${token}` },
    });

    fetchBatches();
    fetchStats();

    // ✅ RESET FORM
    setForm({
      season_id: "",
      batch_no: "",
      input_qty_kg: "",
      start_date: "",
      end_date: "",
      process_type: "Drying",
      output_qty_kg: "",
      status: "In Progress",
      supervisor_id: "",
      notes: "",
    });
  };

  /* ================= CREATE DELIVERY ================= */

  const handleDeliverySubmit = async () => {
    const payload = {
      batch_id: Number(deliveryForm.batch_id),
      season_id: Number(deliveryForm.season_id),
      client_name: deliveryForm.client_name,
      delivery_date: deliveryForm.delivery_date,
      quantity_kg: Number(deliveryForm.quantity_kg),
      rate_per_kg: Number(deliveryForm.rate_per_kg),
      vehicle_no: deliveryForm.vehicle_no,
      payment_status: deliveryForm.payment_status,
      invoice_no: deliveryForm.invoice_no,
    };

    await axios.post(`${API}/production/deliveries`, payload, {
      headers: { Authorization: `Bearer ${token}` },
    });

    fetchDeliveries();
    fetchStats();

    // ✅ RESET DELIVERY FORM
    setDeliveryForm({
      batch_id: "",
      season_id: "",
      client_name: "",
      delivery_date: "",
      quantity_kg: "",
      rate_per_kg: "",
      vehicle_no: "",
      payment_status: "Pending",
      invoice_no: "",
    });
  };

  return (
    <div>
      <PageHeader
        title="Production"
        subtitle="Factory drying process and finished goods tracking"
      >
        <FiRefreshCw className="page-refresh" onClick={refreshToken} />
      </PageHeader>

      <div className="stats-grid mb-24">
        <StatCard label="Sent to Factory" value={stats.sent_to_factory} unit="kg" icon="🏭" />

        <StatCard
          label="In Drying"
          value={stats.in_drying}
          unit="kg"
          icon="🌡️"
          variant="gold"
        />

        <StatCard
          label="Dried / Ready"
          value={stats.dried_ready}
          unit="kg"
          change="+8% yield"
          up
          icon="✅"
          variant="sky"
        />

        <StatCard
          label="Finished Goods"
          value={stats.finished_goods}
          unit="kg"
          change="To Client A"
          up
          icon="📫"
        />
      </div>

      <div className="grid-2 mb-24">
        {/* SEND TO FACTORY */}

        {/* <Card title="Send to Factory">
          <div className="form-grid--double">
            <FormField
              label="Season"
              type="select"
              options={seasons.map((s) => ({
                label: s.season_name,
                value: s.season_id,
              }))}
              value={form.season_id}
              onChange={(e) => setForm({ ...form, season_id: e.target.value })}
            />

            <FormField
              label="Batch Number"
              type="text"
              value={form.batch_no}
              onChange={(e) => setForm({ ...form, batch_no: e.target.value })}
            />

            <FormField
              label="Quantity (kg)"
              type="number"
              value={form.input_qty_kg}
              onChange={(e) =>
                setForm({ ...form, input_qty_kg: e.target.value })
              }
            />

            <FormField
              label="Start Date"
              type="date"
              value={form.start_date}
              onChange={(e) => setForm({ ...form, start_date: e.target.value })}
            />

            <FormField
              label="End Date"
              type="date"
              value={form.end_date}
              onChange={(e) => setForm({ ...form, end_date: e.target.value })}
            />

            <FormField
              label="Process Type"
              type="select"
              options={["Drying", "Cleaning", "Sorting"]}
              value={form.process_type}
              onChange={(e) =>
                setForm({ ...form, process_type: e.target.value })
              }
            />

            <FormField
              label="Output Quantity (kg)"
              type="number"
              value={form.output_qty_kg}
              onChange={(e) =>
                setForm({ ...form, output_qty_kg: e.target.value })
              }
            />

            <FormField
              label="Supervisor ID"
              type="number"
              value={form.supervisor_id}
              onChange={(e) =>
                setForm({ ...form, supervisor_id: e.target.value })
              }
            />

            <FormField
              label="Notes"
              type="textarea"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </div>

          <Button style={{ marginTop: 14 }} onClick={handleSubmit}>
            Dispatch to Factory
          </Button>
        </Card> */}
        <Card title="Send to Factory">
          <div className="form-grid--double">
            {/* <FormField
              label="Season"
              type="select"
              options={seasons.map((s) => ({
                label: s.season_name,
                value: s.season_id,
              }))}
              value={form.season_id}
              onChange={(e) =>
                setForm({ ...form, season_id: e.target.value })
              }
            /> */}

            <FormField
              label="Batch No"
              value={form.batch_no}
              onChange={(e) =>
                setForm({ ...form, batch_no: e.target.value })
              }
            />

            <FormField
              label="Owner Type"
              type="select"
              options={["Own", "External"]}
              value={form.batch_owner_type}
              onChange={(e) =>
                setForm({ ...form, batch_owner_type: e.target.value })
              }
            />

            {form.batch_owner_type === "External" && (
              <FormField
                label="Company"
                type="select"
                options={companies.map((c) => ({
                  label: c.company_name,
                  value: c.company_id,
                }))}
                value={form.company_id}
                onChange={(e) =>
                  setForm({ ...form, company_id: e.target.value })
                }
              />
            )}

            <FormField
              label="Input Qty"
              type="number"
              value={form.input_qty_kg}
              onChange={(e) =>
                setForm({ ...form, input_qty_kg: e.target.value })
              }
            />

            <FormField
              label="Start Date"
              type="date"
              value={form.start_date}
              onChange={(e) =>
                setForm({ ...form, start_date: e.target.value })
              }
            />

            <FormField
              label="End Date"
              type="date"
              value={form.end_date}
              onChange={(e) =>
                setForm({ ...form, end_date: e.target.value })
              }
            />

            <FormField
              label="Process"
              type="select"
              options={["Drying", "Cleaning"]}
              value={form.process_type}
              onChange={(e) =>
                setForm({ ...form, process_type: e.target.value })
              }
            />

            <FormField
              label="Output Qty"
              type="number"
              value={form.output_qty_kg}
              onChange={(e) =>
                setForm({ ...form, output_qty_kg: e.target.value })
              }
            />

            <FormField
              label="Notes"
              type="textarea"
              value={form.notes}
              onChange={(e) =>
                setForm({ ...form, notes: e.target.value })
              }
            />
          </div>
          <Button style={{ marginTop: 14 }} onClick={handleSubmit}>
            Dispatch to Factory
          </Button>
        </Card>


        {/* BATCH TRACKER */}

        <Card title="Factory Batches">
          {batches.map((b, i) => (
            <div className="batch-item" key={i}>
              <span className="batch-item__icon">🏭</span>

              <div className="batch-item__info">
                <div className="batch-item__name">{b.batch_no}</div>

                <div className="batch-item__meta">
                  {b.input_qty_kg} kg · Start {b.start_date} · End {b.end_date}
                </div>
              </div>

              <Badge color={statusColor(b.status)}>{b.status}</Badge>
            </div>
          ))}
        </Card>
      </div>

      {/* DELIVERY FORM */}
      <div className="grid-2 mb-24">
        <Card title="Deliver Finished Goods">
          <div className="form-grid--double">
            <FormField
              label="Batch"
              type="select"
              options={batches.map((b) => ({
                label: b.batch_no,
                value: b.batch_id,
              }))}
              value={deliveryForm.batch_id}
              onChange={(e) =>
                setDeliveryForm({ ...deliveryForm, batch_id: e.target.value })
              }
            />

            <FormField
              label="Season"
              type="select"
              options={seasons.map((s) => ({
                label: s.season_name,
                value: s.season_id,
              }))}
              value={deliveryForm.season_id}
              onChange={(e) =>
                setDeliveryForm({ ...deliveryForm, season_id: e.target.value })
              }
            />

            <FormField
              label="Client Name"
              type="text"
              value={deliveryForm.client_name}
              onChange={(e) =>
                setDeliveryForm({ ...deliveryForm, client_name: e.target.value })
              }
            />

            <FormField
              label="Delivery Date"
              type="date"
              value={deliveryForm.delivery_date}
              onChange={(e) =>
                setDeliveryForm({
                  ...deliveryForm,
                  delivery_date: e.target.value,
                })
              }
            />

            <FormField
              label="Quantity (kg)"
              type="number"
              value={deliveryForm.quantity_kg}
              onChange={(e) =>
                setDeliveryForm({ ...deliveryForm, quantity_kg: e.target.value })
              }
            />

            <FormField
              label="Rate per kg"
              type="number"
              value={deliveryForm.rate_per_kg}
              onChange={(e) =>
                setDeliveryForm({ ...deliveryForm, rate_per_kg: e.target.value })
              }
            />

            <FormField
              label="Vehicle No"
              type="text"
              value={deliveryForm.vehicle_no}
              onChange={(e) =>
                setDeliveryForm({ ...deliveryForm, vehicle_no: e.target.value })
              }
            />

            <FormField
              label="Payment Status"
              type="select"
              options={["Pending", "Paid"]}
              value={deliveryForm.payment_status}
              onChange={(e) =>
                setDeliveryForm({
                  ...deliveryForm,
                  payment_status: e.target.value,
                })
              }
            />

            <FormField
              label="Invoice No"
              type="text"
              value={deliveryForm.invoice_no}
              onChange={(e) =>
                setDeliveryForm({ ...deliveryForm, invoice_no: e.target.value })
              }
            />
          </div>

          <Button style={{ marginTop: 14 }} onClick={handleDeliverySubmit}>
            Deliver to Client
          </Button>
        </Card>

        {/* Timeline */}

        <Card title="Production Timeline" subtitle="Factory Process">
          <div className="timeline">
            {timeline.map((step, i) => {
              const isDone = step.status === "Completed";

              return (
                <div className="timeline__item" key={i}>
                  <div className="timeline__track">
                    <div
                      className={`timeline__dot ${isDone ? "timeline__dot--done" : ""
                        }`}
                    />

                    {i < timeline.length - 1 && (
                      <div className="timeline__line" />
                    )}
                  </div>

                  <div className="timeline__body">
                    <div
                      className={`timeline__title ${isDone ? "" : "timeline__title--pending"
                        }`}
                    >
                      {step.step}
                    </div>

                    <div className="timeline__desc">
                      {step.status}
                    </div>

                    <div className="timeline__date">
                      {step.date || "Pending"}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      </div>
    </div>
  );
}
